import { useEffect, useMemo } from 'react'
import { useViewerPlanSegment } from '@/features/user/settings/sections/plan-change/lib/helpers'

import { StripeInterval } from '@/reducers/subscription.slice'
import { PlanTier, StripeProduct } from '@/actions/settings/billingActions'
import { HEARD_LITE_PRODUCT_IDS } from '@/constants/pricingConstants'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  centsToCurrency,
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { useLoadedFlagValue, useReselector } from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getProductByInterval, signupPlanName } from './helpers'
import { Badge, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { DetailsAccordion } from './DetailsAccordion'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import type { ProductFeatureId } from '@/actions/permissionActions'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { useAnnualCommitmentPrice } from './hooks'
import { DateTime } from 'luxon'
import { fetchFreeTrialLength } from '../helpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectUserIsLateJoiner } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { selectProductDescription } from '@/features/user/signup/selectors'
import { usePayrollSignupPromo } from '../payroll-signup-promo.helpers'

// Lite mirrors the practice-journey Lite card exactly. Essential/Premium
// inherit every Lite section ("Includes: Everything in Lite Plan") and add
// their own tier-specific sections on top.
const LITE_SECTIONS = [
  'bookkeeping' as const,
  'reporting' as const,
  'taxes' as const,
  'support' as const,
]
const STANDARD_SECTIONS = [
  'bookkeeping' as const,
  'reporting' as const,
  'taxes' as const,
  'scorp' as const,
  'group' as const,
  'support' as const,
]

export const PlanDetailsSection = ({
  stripeProducts,
  selectedProduct,
  freeTrialEnabled,
  freeTrialSearchParamValue,
  monthlySavingsOnAnnualPlan,
  productPermissions,
}: {
  stripeProducts: StripeProduct[]
  selectedProduct?: StripeProduct
  freeTrialEnabled: boolean
  freeTrialSearchParamValue: string | null
  monthlySavingsOnAnnualPlan: string | null
  productPermissions?: Record<string, ProductFeatureId[]>
}) => {
  const dispatch = useAppDispatch()

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const annualPlan = getProductByInterval(stripeProducts, StripeInterval.year)
  const isAnnual = selectedProduct === annualPlan
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const { productPriceAsCurrency } = useAnnualCommitmentPrice()
  const isLateJoiner = useReselector(selectUserIsLateJoiner)

  const monthlyDollarsFormatted = useMemo(() => {
    if (selectedProduct?.default_price && isAnnual) {
      return formatCurrency(
        centsToCurrency(selectedProduct.default_price.unit_amount).divide(12),
        { hideDecimalsIfZero: true }
      )
    }
    const productPrice = productPriceAsCurrency(selectedProduct)
    return formatCurrency(productPrice, {
      hideDecimalsIfZero: true,
    })
  }, [selectedProduct, isAnnual, productPriceAsCurrency])

  const isLiteProduct =
    selectedProduct?.metadata?.tier === PlanTier.lite ||
    (selectedProduct?.id
      ? HEARD_LITE_PRODUCT_IDS.includes(selectedProduct.id)
      : false)
  const { payrollPromoTextForTier } = usePayrollSignupPromo()
  const copyFromTier = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.planCopyFromTier,
    false
  )
  const viewerPlanSegment = useViewerPlanSegment()
  const productDescription = useReselector(selectProductDescription, {
    sections: isLiteProduct ? LITE_SECTIONS : STANDARD_SECTIONS,
    permissions: selectedProduct?.id
      ? productPermissions?.[selectedProduct.id]
      : [],
    tier: selectedProduct?.metadata?.tier,
    copyFromTier: Boolean(copyFromTier),
    isLitePlanEnabled: true,
    payrollPromoText: payrollPromoTextForTier(selectedProduct?.metadata?.tier),
  })

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const annualPrice = useMemo(
    () =>
      selectedProduct?.default_price &&
      formatCurrencyFromCents(selectedProduct.default_price.unit_amount, {
        hideDecimalsIfZero: true,
      }),
    [selectedProduct?.default_price]
  )

  const freeTrialDays = fetchFreeTrialLength(freeTrialSearchParamValue)
  const trialEndDate = DateTime.now()
    .plus({ days: freeTrialDays })
    .toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)

  const showFreeTrialAnnualVersion =
    freeTrialEnabled && isAnnual && !hasAnnualCommitment

  const monthlyPlan = getProductByInterval(stripeProducts, StripeInterval.month)

  return (
    <div className="flex flex-col">
      <div className="flex mb-4">
        <Text
          className="w-1/2"
          color={TextColors.MUTED}
          variant={TypographyStyles.EYEBROW}
        >
          {isAnnual ? 'annual plan' : 'monthly plan'}
        </Text>

        <Text
          className="w-1/2 text-right"
          color={TextColors.MUTED}
          variant={TypographyStyles.EYEBROW}
        >
          {!isAnnual &&
            !isLateJoiner &&
            !hasAnnualCommitment &&
            'cancel anytime'}
        </Text>
      </div>

      <div className="flex mb-2">
        <Text
          className="w-[56.25%]"
          variant={isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_SM}
        >
          {signupPlanName(selectedProduct, viewerPlanSegment)}
        </Text>

        <div className="w-[43.75%] text-right relative">
          {showFreeTrialAnnualVersion && monthlySavingsOnAnnualPlan && (
            <Badge color="green" variant="secondary" className="mr-2">
              Save {monthlySavingsOnAnnualPlan}/mo!
            </Badge>
          )}

          <Text
            className="inline"
            variant={
              isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_SM
            }
          >
            {showFreeTrialAnnualVersion ? annualPrice : monthlyDollarsFormatted}
          </Text>

          <Text
            className="inline"
            variant={
              isMobile ? TypographyStyles.H3 : TypographyStyles.DISPLAY_XXS
            }
          >
            {showFreeTrialAnnualVersion ? '/yr' : '/mo'}
          </Text>

          <div className="flex flex-col items-end gap-0.5 mt-0.5">
            {hasAnnualCommitment && !isAnnual && (
              <Text
                color={TextColors.MUTED}
                variant={TypographyStyles.BODY_MD}
                className="line-through"
                textAlign="right"
              >
                {formatCurrencyFromCents(
                  selectedProduct?.default_price.unit_amount,
                  {
                    hideDecimalsIfZero: true,
                  }
                )}{' '}
                /mo
              </Text>
            )}
            {isAnnual && monthlyPlan?.default_price?.unit_amount && (
              <Text
                color={TextColors.MUTED}
                variant={TypographyStyles.BODY_MD}
                className="line-through"
                textAlign="right"
              >
                {formatCurrencyFromCents(
                  monthlyPlan.default_price.unit_amount,
                  {
                    hideDecimalsIfZero: true,
                  }
                )}{' '}
                /mo
              </Text>
            )}

            {isAnnual && (
              <Text
                color={TextColors.MUTED}
                variant={TypographyStyles.BODY_XS}
                textAlign="right"
              >
                {isAnnual
                  ? freeTrialEnabled
                    ? `Billed annually starting ${trialEndDate}`
                    : `Billed ${annualPrice} annually`
                  : 'Billed monthly'}
              </Text>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-fit">
        <DetailsAccordion productDescription={productDescription} />
      </div>
    </div>
  )
}
